/**
 * MiniDiagnosticExecutiveSummary
 * ─────────────────────────────
 * 2026-07-17 — Compact 12-section executive summary that renders at
 * the top of the Mini Station Diagnostic modal.
 *
 * Every section pulls from `data.executive_summary` produced by the
 * backend `mini_diagnostic_executive_summary.py` composer. If a
 * section is `insufficient_evidence: true`, the UI states that
 * explicitly rather than guessing.
 */
import React from 'react';
import {
  CheckCircle2, XCircle, AlertTriangle, ArrowUp, ArrowDown, Minus,
  Activity, Music, Radio, Search, Shield, Zap, Repeat, ClipboardList,
} from 'lucide-react';


/* ── Tiny building blocks ───────────────────────────────────────── */

const Pill = ({ children, tone = 'slate', className = '' }) => {
  const tones = {
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${tones[tone] || tones.slate} ${className}`}>
      {children}
    </span>
  );
};

const Card = ({ title, icon: Icon, tone = 'slate', children, testid }) => {
  const border = {
    green: 'border-emerald-200 dark:border-emerald-800',
    amber: 'border-amber-200 dark:border-amber-800',
    red: 'border-red-200 dark:border-red-800',
    slate: 'border-slate-200 dark:border-slate-700',
  };
  return (
    <div
      className={`bg-white dark:bg-slate-900/40 border ${border[tone] || border.slate} rounded-lg p-2.5`}
      data-testid={testid}
    >
      {title && (
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-1.5">
          {Icon && <Icon className="w-3 h-3" />}
          {title}
        </div>
      )}
      {children}
    </div>
  );
};

const KV = ({ label, value, tone }) => (
  <div className="flex items-baseline justify-between text-xs py-0.5">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className={`font-semibold ${tone === 'red' ? 'text-red-600 dark:text-red-400' : tone === 'green' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-slate-200'}`}>
      {value ?? '—'}
    </span>
  </div>
);


/* ── The 12 sections ────────────────────────────────────────────── */

const HealthCard = ({ s }) => {
  const status = (s?.health_status || 'UNKNOWN').toUpperCase();
  const tone = status === 'HEALTHY' ? 'green' : status === 'WARNING' ? 'amber' : status === 'CRITICAL' ? 'red' : 'slate';
  const Icon = tone === 'green' ? CheckCircle2 : tone === 'red' ? XCircle : AlertTriangle;
  return (
    <Card title="§1 · Recording Health" icon={Activity} tone={tone} testid="exec-section-1-health">
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tone === 'green' ? 'text-emerald-500' : tone === 'red' ? 'text-red-500' : tone === 'amber' ? 'text-amber-500' : 'text-slate-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Pill tone={tone}>{status}</Pill>
            <Pill tone="slate">Confidence: {s?.confidence || 'LOW'}</Pill>
          </div>
          <div className="text-xs text-slate-700 dark:text-slate-300 mt-1.5 leading-snug">
            {s?.primary_reason || '—'}
          </div>
        </div>
      </div>
    </Card>
  );
};

const CoverageCard = ({ s }) => {
  const cov = s?.coverage_pct;
  const tone = cov == null ? 'slate' : cov >= 70 ? 'green' : cov >= 40 ? 'amber' : 'red';
  const trendTone = s?.trend === 'improving' ? 'green' : s?.trend === 'degrading' ? 'red' : 'slate';
  const TrendIcon = s?.trend === 'improving' ? ArrowUp : s?.trend === 'degrading' ? ArrowDown : Minus;
  return (
    <Card title="§2 · Recording Coverage" icon={Music} tone={tone} testid="exec-section-2-coverage">
      <KV label="Καταγράφηκαν σήμερα" value={s?.recorded_today ?? '—'} />
      <KV label="Αναμενόμενα" value={s?.expected_today ?? '—'} />
      <KV label="Κάλυψη" value={cov != null ? `${cov}%` : '—'} tone={tone === 'slate' ? undefined : tone === 'green' ? 'green' : tone === 'amber' ? 'amber' : 'red'} />
      <div className="flex items-center justify-between text-xs pt-0.5">
        <span className="text-slate-500 dark:text-slate-400">Trend (vs 24h)</span>
        <span className={`inline-flex items-center gap-1 font-semibold ${trendTone === 'green' ? 'text-emerald-600' : trendTone === 'red' ? 'text-red-600' : 'text-slate-600'}`}>
          <TrendIcon className="w-3 h-3" />
          {s?.trend_label || s?.trend || '—'}
        </span>
      </div>
    </Card>
  );
};

const PipelineCard = ({ s }) => {
  const result = s?.result || 'Pending';
  const tone = result === 'Saved' ? 'green' : result === 'Rejected' ? 'red' : result === 'Merged' ? 'amber' : 'slate';
  return (
    <Card title="§3 · Recording Pipeline" icon={Zap} tone={tone} testid="exec-section-3-pipeline">
      <div className="flex flex-wrap items-center gap-1 mb-1.5">
        {(s?.stages || []).map((st, i) => (
          <React.Fragment key={st.name}>
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${st.reached ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}
              title={st.reason || (st.reached ? 'Reached' : 'Not reached')}
            >
              {st.reached ? <CheckCircle2 className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
              {st.name}
            </span>
            {i < (s.stages.length - 1) && <span className="text-slate-400 text-[10px]">›</span>}
          </React.Fragment>
        ))}
      </div>
      <KV label="Αποτέλεσμα" value={result} tone={tone === 'green' ? 'green' : tone === 'red' ? 'red' : tone === 'amber' ? 'amber' : undefined} />
    </Card>
  );
};

const DominantLossCard = ({ s }) => {
  if (s?.insufficient_evidence) {
    return (
      <Card title="§4 · Dominant Loss" icon={Search} tone="slate" testid="exec-section-4-loss">
        <div className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
          {s?.note || 'Insufficient evidence.'}
        </div>
      </Card>
    );
  }
  return (
    <Card title="§4 · Dominant Loss" icon={Search} tone="amber" testid="exec-section-4-loss">
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        <Pill tone="amber">{s?.human_label}</Pill>
        {s?.stage && <Pill tone="slate">{s.stage}</Pill>}
      </div>
      <KV label="Events" value={s?.count ?? '—'} />
      {s?.pct_of_classified != null && (
        <KV label="% ταξινομημένων" value={`${s.pct_of_classified}%`} />
      )}
      {s?.recommendation && (
        <div className="text-[11px] text-slate-600 dark:text-slate-400 mt-1 leading-snug italic">
          {s.recommendation}
        </div>
      )}
    </Card>
  );
};

const LastDecisionCard = ({ s }) => {
  if (s?.insufficient_evidence) {
    return (
      <Card title="§5 · Last Recording Decision" icon={Music} tone="slate" testid="exec-section-5-last-decision">
        <div className="text-xs text-slate-500 dark:text-slate-400">{s?.note || 'Insufficient evidence.'}</div>
      </Card>
    );
  }
  const dec = (s?.decision || '').toLowerCase();
  const tone = dec === 'saved' ? 'green' : dec === 'rejected' ? 'red' : dec === 'merged' ? 'amber' : 'slate';
  return (
    <Card title="§5 · Last Recording Decision" icon={Music} tone={tone} testid="exec-section-5-last-decision">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono text-sm text-slate-800 dark:text-slate-100">{s?.time_label || '—'}</span>
        <Pill tone={tone}>{s?.decision || '—'}</Pill>
      </div>
      <div className="text-xs text-slate-800 dark:text-slate-200 font-semibold truncate">
        {s?.artist || '—'}
      </div>
      <div className="text-xs text-slate-600 dark:text-slate-400 truncate">
        {s?.title || '—'}
      </div>
      {s?.reason && s.reason !== 'OK' && (
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
          Reason: {s.reason}
        </div>
      )}
    </Card>
  );
};

const RuntimeCard = ({ s }) => (
  <Card title="§6 · Recognition Runtime" icon={Radio} tone="slate" testid="exec-section-6-runtime">
    <KV label="Mode" value={s?.mode || '—'} />
    <KV label="Provider" value={s?.provider || '—'} />
    <KV label="Primary" value={s?.primary_method || '—'} />
    <KV label="Current source" value={s?.current_source || 'not_available'} />
    {s?.fallback && <KV label="Fallback" value={s.fallback} />}
  </Card>
);

const RecentRecognitionCard = ({ s }) => {
  if (s?.insufficient_evidence) {
    return (
      <Card title="§7 · Recent Recognition (last 20)" icon={Activity} tone="slate" testid="exec-section-7-recent-reco">
        <div className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
          {s?.note || 'Insufficient evidence.'}
        </div>
      </Card>
    );
  }
  return (
    <Card title={`§7 · Recent Recognition (last ${s?.attempts_seen}/${s?.attempts_requested})`} icon={Activity} tone="slate" testid="exec-section-7-recent-reco">
      <div className="grid grid-cols-2 gap-x-3">
        <KV label="Success" value={s?.success ?? 0} tone="green" />
        <KV label="Deferred" value={s?.deferred ?? 0} />
        <KV label="No Match" value={s?.nomatch ?? 0} />
        <KV label="Exceptions" value={s?.exceptions ?? 0} tone={s?.exceptions ? 'red' : undefined} />
      </div>
      {s?.average_duration_sec != null && (
        <KV label="Avg duration" value={`${s.average_duration_sec} sec`} />
      )}
    </Card>
  );
};

const PersistenceCard = ({ s }) => {
  const tone = s?.rejected > 0 ? 'red' : s?.merged > 0 ? 'amber' : s?.saved > 0 ? 'green' : 'slate';
  return (
    <Card title="§8 · Persistence (last 1h)" icon={Shield} tone={tone} testid="exec-section-8-persistence">
      <div className="grid grid-cols-3 gap-x-2">
        <KV label="Saved" value={s?.saved ?? 0} tone="green" />
        <KV label="Merged" value={s?.merged ?? 0} tone="amber" />
        <KV label="Rejected" value={s?.rejected ?? 0} tone={s?.rejected ? 'red' : undefined} />
      </div>
    </Card>
  );
};

const DuplicateWatchCard = ({ s }) => {
  if (!s?.detected) {
    return (
      <Card title="§9 · Duplicate Session Watch" icon={Repeat} tone="green" testid="exec-section-9-duplicate">
        <div className="text-xs text-emerald-700 dark:text-emerald-400">
          {s?.message || 'No duplicate session patterns detected.'}
        </div>
      </Card>
    );
  }
  return (
    <Card title="§9 · Duplicate Session Watch" icon={Repeat} tone="red" testid="exec-section-9-duplicate">
      <div className="flex items-center gap-1.5 mb-1">
        <Pill tone="red">{s.count} songs</Pill>
        {s.pattern && <Pill tone="amber">{s.pattern}</Pill>}
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400">
        Repeated rejected sessions for the same song.
      </div>
    </Card>
  );
};

const TruthCard = ({ text }) => (
  <Card title="§10 · Recording Truth Summary" icon={ClipboardList} tone="slate" testid="exec-section-10-truth">
    <div className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
      {text || '—'}
    </div>
  </Card>
);

const RecommendationCard = ({ s }) => {
  const tone = /No further/i.test(s?.action || '') ? 'green' : 'amber';
  return (
    <Card title="§11 · Investigation Recommendation" icon={Search} tone={tone} testid="exec-section-11-recommendation">
      <div className={`text-xs font-semibold ${tone === 'green' ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-800 dark:text-amber-300'}`}>
        {s?.action || '—'}
      </div>
      {s?.trigger && (
        <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
          trigger: {s.trigger}
        </div>
      )}
    </Card>
  );
};


/* ── Composed executive summary ─────────────────────────────────── */

const MiniDiagnosticExecutiveSummary = ({ es }) => {
  if (!es || !es.section_1_health_summary) return null;
  return (
    <div className="space-y-2" data-testid="mini-diagnostic-executive-summary">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
          Executive Summary
        </div>
        <Pill tone="blue">{'< 30s scan'}</Pill>
      </div>

      {/* Grid: 2 columns on wider screens */}
      <div className="grid grid-cols-1 gap-2">
        <HealthCard s={es.section_1_health_summary} />
        <CoverageCard s={es.section_2_recording_coverage} />
        <PipelineCard s={es.section_3_pipeline_summary} />
        <DominantLossCard s={es.section_4_dominant_loss} />
        <LastDecisionCard s={es.section_5_last_decision} />
        <RuntimeCard s={es.section_6_recognition_runtime} />
        <RecentRecognitionCard s={es.section_7_recent_recognition_health} />
        <PersistenceCard s={es.section_8_persistence_summary} />
        <DuplicateWatchCard s={es.section_9_duplicate_session_watch} />
        <TruthCard text={es.section_10_truth_summary} />
        <RecommendationCard s={es.section_11_investigation_recommendation} />
      </div>

      <div className="text-[10px] text-slate-500 dark:text-slate-400 italic px-1 pt-0.5">
        §12 · {es.evidence_note}
      </div>
    </div>
  );
};

export default MiniDiagnosticExecutiveSummary;
